import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import ReactDOM from "react-dom";
import { language } from "../../../languageProvider/language/language";
import {
  defaultLanguage,
  iconsObj,
  INTERACTIVITY_TYPE_TO_LABEL_KEY,
} from "../../../helper/utility";
import useFilterCounts from "./useFilterCounts";

const L = (key, fallback) =>
  (language[defaultLanguage] && language[defaultLanguage][key]) || fallback;

/**
 * Mobile bottom-sheet that replaces the legacy OffCanvas filter UI.
 *
 * Sections (rendered conditionally if the corresponding list is non-empty):
 *   - Tag
 *   - Promo
 *   - Marchi (brands)
 *
 * Selection is staged into "pending" sets and only committed to the parent
 * when the user presses "Applica filtri (n)". Closing the sheet (via swipe,
 * backdrop tap, ESC, X button) preserves the pending state — the sheet is
 * always mounted in the DOM, opened/closed via a CSS class + transform.
 */
const FiltersBottomSheet = ({
  open,
  onClose,
  onApply,
  initialFilters,
  tags = [],
  promos = [],
  brands = [],
  hasOnSale = false,
  interactivities = [],
  products = [],
  categoryFilter = "",
  nameFilter = "",
}) => {
  const [pendingTags, setPendingTags] = useState(
    () => new Set(initialFilters?.tags || [])
  );
  const [pendingPromos, setPendingPromos] = useState(
    () => new Set(initialFilters?.promos || [])
  );
  const [pendingBrands, setPendingBrands] = useState(
    () => new Set(initialFilters?.brands || [])
  );
  const [pendingOnSale, setPendingOnSale] = useState(
    () => Boolean(initialFilters?.onSale)
  );
  const [pendingInter, setPendingInter] = useState(
    () => new Set(initialFilters?.interactivities || [])
  );

  const [dragY, setDragY] = useState(0);
  const [isDragging, setIsDragging] = useState(false);

  const sheetRef = useRef(null);
  const closeBtnRef = useRef(null);
  const prevOpenRef = useRef(false);
  const dragState = useRef({ startY: 0, startTime: 0, lastY: 0 });

  // Sync pending sets when the sheet opens (initialFilters is the source of
  // truth at open-time, so the user starts editing from the currently applied
  // filter state). Closed -> open transition only.
  useEffect(() => {
    if (open && !prevOpenRef.current) {
      setPendingTags(new Set(initialFilters?.tags || []));
      setPendingPromos(new Set(initialFilters?.promos || []));
      setPendingBrands(new Set(initialFilters?.brands || []));
      setPendingOnSale(Boolean(initialFilters?.onSale));
      setPendingInter(new Set(initialFilters?.interactivities || []));
      setDragY(0);
      setIsDragging(false);
    }
    prevOpenRef.current = open;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Body lock while open (CSS sets overflow:hidden; touch-action:none).
  useLayoutEffect(() => {
    if (open) {
      document.body.classList.add("pf-sheet-open");
    } else {
      document.body.classList.remove("pf-sheet-open");
    }
    return () => {
      document.body.classList.remove("pf-sheet-open");
    };
  }, [open]);

  // Focus the close button when the sheet opens (simple focus trap entry).
  useEffect(() => {
    if (open && closeBtnRef.current) {
      // Defer to allow the transition class to settle.
      const id = window.setTimeout(() => {
        if (closeBtnRef.current) closeBtnRef.current.focus();
      }, 40);
      return () => window.clearTimeout(id);
    }
    return undefined;
  }, [open]);

  // ESC to close.
  useEffect(() => {
    if (!open) return undefined;
    const handler = (e) => {
      if (e.key === "Escape") {
        e.stopPropagation();
        onClose && onClose();
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [open, onClose]);

  const counts = useFilterCounts({
    products,
    categoryFilter,
    nameFilter,
    pendingTags,
    pendingPromos,
    pendingBrands,
    pendingOnSale,
    pendingInteractivities: pendingInter,
  });

  const togglePending = useCallback((setter, value) => {
    setter((prev) => {
      const next = new Set(prev);
      if (next.has(value)) next.delete(value);
      else next.add(value);
      return next;
    });
  }, []);

  const handleClear = useCallback(() => {
    setPendingTags(new Set());
    setPendingPromos(new Set());
    setPendingBrands(new Set());
    setPendingOnSale(false);
    setPendingInter(new Set());
  }, []);

  const handleApply = useCallback(() => {
    if (counts.totalCount === 0) return;
    onApply &&
      onApply({
        tags: new Set(pendingTags),
        promos: new Set(pendingPromos),
        brands: new Set(pendingBrands),
        onSale: Boolean(pendingOnSale),
        interactivities: new Set(pendingInter),
      });
    onClose && onClose();
  }, [
    counts.totalCount,
    onApply,
    onClose,
    pendingTags,
    pendingPromos,
    pendingBrands,
    pendingOnSale,
    pendingInter,
  ]);

  // ---- Drag-to-dismiss (header only) ----
  const onTouchStart = useCallback((e) => {
    if (!e.touches || e.touches.length === 0) return;
    const y = e.touches[0].clientY;
    dragState.current.startY = y;
    dragState.current.lastY = y;
    dragState.current.startTime = Date.now();
    setIsDragging(true);
  }, []);

  const onTouchMove = useCallback(
    (e) => {
      if (!isDragging || !e.touches || e.touches.length === 0) return;
      const y = e.touches[0].clientY;
      dragState.current.lastY = y;
      const dy = Math.max(0, y - dragState.current.startY);
      setDragY(dy);
    },
    [isDragging]
  );

  const onTouchEnd = useCallback(() => {
    if (!isDragging) return;
    const elapsed = Math.max(1, Date.now() - dragState.current.startTime);
    const dy = Math.max(0, dragState.current.lastY - dragState.current.startY);
    const velocity = dy / elapsed; // px/ms
    setIsDragging(false);
    if (dy > 120 || velocity > 0.6) {
      // Animate to closed (CSS transition takes over once isDragging=false).
      setDragY(0);
      onClose && onClose();
    } else {
      setDragY(0);
    }
  }, [isDragging, onClose]);

  // ---- Labels ----
  const filtriLabel = L("filtri", "Filtri");
  const nessunFiltroLabel = L(
    "nessun_filtro_selezionato",
    "Nessun filtro selezionato"
  );
  const nFiltriTemplate = L("n_filtri_selezionati", "{n} filtri selezionati");
  const cancellaLabel = L("cancella", "Cancella");
  const applicaTemplate = L("applica_filtri", "Applica filtri ({n})");
  const nessunRisultatoLabel = L("nessun_risultato", "Nessun risultato");
  const nessunaCombinazioneLabel = L(
    "nessun_prodotto_combinazione",
    "Nessun prodotto con questa combinazione di filtri"
  );
  const tagSectionLabel = L("tag_section", "Tag");
  const promoSectionLabel = L("promo_section", "Promo");
  const marchiSectionLabel = L("marchi_section", "Marchi");
  const offerteSectionLabel = L("offerte", "Offerte");
  const interattivitaSectionLabel = L("interattivita_section", "Interattività");
  const chiudiFiltriLabel = L("chiudi_filtri", "Chiudi filtri");

  const totalPending =
    pendingTags.size +
    pendingPromos.size +
    pendingBrands.size +
    (pendingOnSale ? 1 : 0) +
    pendingInter.size;
  const subtitle =
    totalPending === 0
      ? nessunFiltroLabel
      : nFiltriTemplate.replace("{n}", String(totalPending));

  const hasTags = Array.isArray(tags) && tags.length > 0;
  const hasPromos = Array.isArray(promos) && promos.length > 0;
  const hasBrands = Array.isArray(brands) && brands.length > 0;
  const hasInter = Array.isArray(interactivities) && interactivities.length > 0;

  const applyDisabled = counts.totalCount === 0;
  const applyLabel = applyDisabled
    ? nessunRisultatoLabel
    : applicaTemplate.replace("{n}", String(counts.totalCount));

  // Sheet transform: closed (translateY(100%)) vs open (translateY(0) + drag).
  // While dragging we disable transition for a 1:1 finger feel; we use a
  // separate class so React handles the transition end naturally on release.
  const sheetStyle = open
    ? {
        transform: `translateY(${dragY}px)`,
        transition: isDragging
          ? "none"
          : "transform .28s cubic-bezier(.32, .72, 0, 1)",
      }
    : undefined;

  // Backdrop opacity attenuates with drag distance (max 200px range).
  const backdropOpacity = open
    ? Math.max(0, 1 - dragY / 200)
    : 0;

  const renderChip = (opts) => {
    const {
      key,
      label,
      image,
      iconNode,
      count,
      selected,
      onClick,
      title,
    } = opts;
    const disabled = !selected && count === 0;
    const classes = [
      "pf-chip-filter",
      selected ? "pf-chip-filter--selected" : "",
      disabled ? "pf-chip-filter--disabled" : "",
    ]
      .filter(Boolean)
      .join(" ");
    return (
      <button
        key={key}
        type="button"
        className={classes}
        aria-pressed={selected}
        disabled={disabled}
        onClick={onClick}
        title={title || label || ""}
      >
        {image ? (
          <img
            className="pf-chip-filter__img"
            src={image}
            alt=""
            aria-hidden="true"
          />
        ) : null}
        {!image && iconNode ? (
          <span className="pf-chip-filter__icon" aria-hidden="true">
            {iconNode}
          </span>
        ) : null}
        {label ? (
          <span className="pf-chip-filter__label">{label}</span>
        ) : null}
        <span className="pf-chip-filter__count">({count})</span>
      </button>
    );
  };

  const node = (
    <div
      className={`pf-sheet-root ${open ? "pf-sheet-root--open" : ""}`}
      aria-hidden={!open}
    >
      <div
        className={`pf-sheet-backdrop ${
          open ? "pf-sheet-backdrop--open" : ""
        }`}
        style={open ? { opacity: backdropOpacity } : undefined}
        onClick={() => onClose && onClose()}
      />

      <div
        ref={sheetRef}
        className={`pf-sheet ${open ? "pf-sheet--open" : ""}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby="pf-sheet-title"
        style={sheetStyle}
      >
        <div
          className="pf-sheet__grabber-zone"
          onTouchStart={onTouchStart}
          onTouchMove={onTouchMove}
          onTouchEnd={onTouchEnd}
          onTouchCancel={onTouchEnd}
        >
          <span className="pf-sheet__grabber" aria-hidden="true" />
        </div>

        <div
          className="pf-sheet__header"
          onTouchStart={onTouchStart}
          onTouchMove={onTouchMove}
          onTouchEnd={onTouchEnd}
          onTouchCancel={onTouchEnd}
        >
          <div className="pf-sheet__header-text">
            <h2 id="pf-sheet-title" className="pf-sheet__title">
              {filtriLabel}
            </h2>
            <p className="pf-sheet__subtitle">{subtitle}</p>
          </div>
          <button
            ref={closeBtnRef}
            type="button"
            className="pf-sheet__close"
            aria-label={chiudiFiltriLabel}
            onClick={() => onClose && onClose()}
          >
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
              focusable="false"
            >
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>

        <div className="pf-sheet__body">
          {!hasTags && !hasPromos && !hasBrands && !hasOnSale && !hasInter && (
            <div className="pf-sheet__empty" role="status">
              {L(
                "nessun_filtro_disponibile",
                "Nessun filtro disponibile per questa pubblicazione."
              )}
            </div>
          )}
          {hasTags && (
            <section className="pf-sheet__section">
              <div className="pf-sheet__section-label">{tagSectionLabel}</div>
              <div className="pf-sheet__chips">
                {tags.map((t) =>
                  renderChip({
                    key: `tag_${t.id}`,
                    label: t.nome,
                    image: t.immagine,
                    count: counts.getTagCount(t.id),
                    selected: pendingTags.has(t.id),
                    onClick: () => togglePending(setPendingTags, t.id),
                    title: t.nome,
                  })
                )}
              </div>
            </section>
          )}

          {hasPromos && (
            <section className="pf-sheet__section">
              <div className="pf-sheet__section-label">
                {promoSectionLabel}
              </div>
              <div className="pf-sheet__chips">
                {promos.map((p) =>
                  renderChip({
                    key: `promo_${p.id}`,
                    label: p.nome,
                    image: p.img,
                    count: counts.getPromoCount(p.id),
                    selected: pendingPromos.has(p.id),
                    onClick: () => togglePending(setPendingPromos, p.id),
                    title: p.nome,
                  })
                )}
              </div>
            </section>
          )}

          {hasBrands && (
            <section className="pf-sheet__section">
              <div className="pf-sheet__section-label">
                {marchiSectionLabel}
              </div>
              <div className="pf-sheet__chips">
                {brands.map((b) =>
                  renderChip({
                    key: `brand_${b.name}`,
                    label: b.name,
                    image: b.logo,
                    count: counts.getBrandCount(b.name),
                    selected: pendingBrands.has(b.name),
                    onClick: () => togglePending(setPendingBrands, b.name),
                    title: b.name,
                  })
                )}
              </div>
            </section>
          )}

          {hasOnSale && (
            <section className="pf-sheet__section">
              <div className="pf-sheet__section-label">
                {offerteSectionLabel}
              </div>
              <div className="pf-sheet__chips">
                {renderChip({
                  key: "onsale_toggle",
                  label: offerteSectionLabel,
                  image: null,
                  count: counts.getOnSaleCount(),
                  selected: pendingOnSale,
                  onClick: () => setPendingOnSale((v) => !v),
                  title: offerteSectionLabel,
                })}
              </div>
            </section>
          )}

          {hasInter && (
            <section className="pf-sheet__section">
              <div className="pf-sheet__section-label">
                {interattivitaSectionLabel}
              </div>
              <div className="pf-sheet__chips">
                {interactivities.map((i) => {
                  const labelKey =
                    INTERACTIVITY_TYPE_TO_LABEL_KEY[i.type] || i.type;
                  const label = L(labelKey, i.type);
                  const iconEntry = iconsObj[i.type];
                  const iconNode = iconEntry && iconEntry.component;
                  return renderChip({
                    key: `inter_${i.type}`,
                    label,
                    image: null,
                    iconNode,
                    count: counts.getInteractivityCount(i.type),
                    selected: pendingInter.has(i.type),
                    onClick: () => togglePending(setPendingInter, i.type),
                    title: label,
                  });
                })}
              </div>
            </section>
          )}
        </div>

        {applyDisabled && (
          <div className="pf-sheet__warn" role="status">
            {nessunaCombinazioneLabel}
          </div>
        )}

        <div className="pf-sheet__footer">
          <button
            type="button"
            className="pf-sheet__btn-cancel"
            onClick={handleClear}
            disabled={totalPending === 0}
          >
            {cancellaLabel}
          </button>
          <button
            type="button"
            className={`pf-sheet__btn-apply ${
              applyDisabled ? "pf-sheet__btn-apply--disabled" : ""
            }`}
            onClick={handleApply}
            disabled={applyDisabled}
            aria-disabled={applyDisabled}
          >
            {applyLabel}
          </button>
        </div>
      </div>
    </div>
  );

  if (typeof document === "undefined") return null;
  return ReactDOM.createPortal(node, document.body);
};

export default FiltersBottomSheet;
